import type { AggregatedStyleValues } from '../../common/abstractStyles/types';
import type {
  DefinitionType,
  VariableType,
} from '@wix/editor-elements-panel-service';
import { FontFamily, FontWeight, CssVariable } from '@wix/css-property-parser';
import { TEXT_PROPERTIES } from './constants';
import { TextControlPartials } from '../types';
import { getShorthandAtomics } from '../../common/grouping/propertyRelationships';
import { CSS_PROPERTIES } from '@wix/component-protocol/schema';

type TextProperty = (typeof TEXT_PROPERTIES)[number];

/**
 * Namespace tracker for cssCustomProperties routing.
 *
 * Maps cssPropertyType → original cssCustomProperty key.
 * Example: { color: '--my-color', fontSize: '--my-font-size' }
 *
 * When a control property changes:
 * - If property is NOT in tracker → write to cssProperties[property]
 * - If property IS in tracker → write to cssCustomProperties[tracker[property]]
 */
export interface NamespaceTracker {
  [cssPropertyType: string]: string; // cssPropertyType → original cssCustomProperty key
}

/**
 * Text control props (subset of what we'll use)
 */
export interface TextControlGroupProps {
  customPanelProps: {
    cssProperties: Record<string, string>;
    cssCustomProperties: Record<string, string>;
  };
  mixedProperties?: Set<string>;
  disabledProperties?: Set<string>;
}

/**
 * Check property eligibility and mixed state based on decomposed values.
 *
 * Returns:
 * - isEligible: true if property exists in decomposed (even with empty array)
 * - isMixed: true if multiple different values exist in decomposed
 */
function checkPropertyMixedState(
  values: AggregatedStyleValues,
  property: TextProperty,
): boolean {
  const uniqueValues = new Set<string>();

  // Check eligibility by looking for property in decomposed (even if empty array)
  if (values.mode === 'cssProperties' || values.mode === 'mixed') {
    if (values.decomposed.cssProperties[property] !== undefined) {
      // Collect unique values for mixed detection
      values.decomposed.cssProperties[property]?.forEach((item) => {
        uniqueValues.add(item.value);
      });
    }
  }
  if (values.mode === 'cssCustomProperties' || values.mode === 'mixed') {
    if (values.decomposed.cssCustomProperties[property] !== undefined) {
      // Collect unique values for mixed detection
      values.decomposed.cssCustomProperties[property]?.forEach((item) => {
        uniqueValues.add(item.value);
      });
    }
  }

  // Check for font shorthand in raw values
  if (property === 'font') {
    if (values.mode === 'cssProperties' && values.raw.cssProperties[property]) {
      uniqueValues.add(values.raw.cssProperties[property]);
    }
    if (values.mode === 'cssCustomProperties') {
      // Find the cssCustomProperty that maps to 'font'
      const customPropEntry = Object.entries(
        values.definitions.cssCustomProperties,
      ).find(([_key, def]) => def.cssPropertyType === property);
      if (customPropEntry) {
        const value = values.raw.cssCustomProperties[customPropEntry[0]];
        if (value) {
          uniqueValues.add(value);
        }
      }
    }
  }

  return uniqueValues.size > 1;
}

export const getCssPropertyTypes = (
  values: AggregatedStyleValues,
): Set<string> => {
  const cssPropertyTypes = new Set<string>();
  if (values.mode === 'cssProperties' || values.mode === 'mixed') {
    Object.keys(values.definitions.cssProperties).forEach((type) => {
      cssPropertyTypes.add(type);
    });
  }
  if (values.mode === 'cssCustomProperties' || values.mode === 'mixed') {
    Object.values(values.definitions.cssCustomProperties).forEach((def) => {
      if (def.cssPropertyType) {
        cssPropertyTypes.add(def.cssPropertyType);
      }
    });
  }
  return cssPropertyTypes;
};

const checkPropertyEligibility = (
  cssPropertyTypes: Set<string>,
  property: TextProperty,
): boolean => {
  if (cssPropertyTypes.has(property)) {
    return true;
  }

  if (
    getShorthandAtomics(CSS_PROPERTIES.CSS_PROPERTY_TYPE.font).includes(
      property,
    )
  ) {
    return cssPropertyTypes.has(CSS_PROPERTIES.CSS_PROPERTY_TYPE.font);
  }

  return false;
};

/**
 * Props mapper: AggregatedStyleValues → TextControlGroup props
 *
 * New contract: Pass styles as-is, only map customProps to props.
 * No decomposition - cssCustomProperties are mapped to props directly.
 * Eligibility and mixed state are still checked using decomposed values.
 */
export const mapTextProps = (
  values: AggregatedStyleValues,
): {
  props: TextControlGroupProps;
  namespaceTracker: NamespaceTracker;
} => {
  const cssProperties: Record<string, string> = {};
  const namespaceTracker: NamespaceTracker = {};
  const mixedProperties = new Set<string>();
  const disabledProperties = new Set<string>();

  const cssPropertyTypes = getCssPropertyTypes(values);

  // Check eligibility and mixed state for each text property
  TEXT_PROPERTIES.forEach((property) => {
    if (!checkPropertyEligibility(cssPropertyTypes, property)) {
      disabledProperties.add(property);
      return;
    }

    if (checkPropertyMixedState(values, property)) {
      mixedProperties.add(property);
    }
  });

  // Map cssCustomProperties to props based on their cssPropertyType
  if (values.mode !== 'cssProperties') {
    Object.entries(values.definitions.cssCustomProperties).forEach(
      ([customPropKey, definition]) => {
        if (definition?.cssPropertyType) {
          const cssPropertyType = definition.cssPropertyType;
          const value = values.raw.cssCustomProperties[customPropKey];

          if (value !== undefined) {
            // Map customProp to its cssPropertyType in props
            cssProperties[cssPropertyType] = value;
            // Track the original customProp key for reverse mapping
            namespaceTracker[cssPropertyType] = customPropKey;
          }
        }
      },
    );
  }

  // Add cssProperties as-is
  if (values.mode !== 'cssCustomProperties') {
    Object.entries(values.raw.cssProperties).forEach(([property, value]) => {
      // Only add if not already added from cssCustomProperties
      if (cssProperties[property] === undefined) {
        cssProperties[property] = value;
      }
    });
  }

  return {
    props: {
      customPanelProps: {
        cssProperties,
        cssCustomProperties: {}, // Empty - we map customProps to cssProperties
      },
      mixedProperties,
      disabledProperties,
    },
    namespaceTracker,
  };
};

/**
 * Convert partials value to CSS string.
 * Reuses existing converters from @wix/css-property-parser (same as parser.ts pattern).
 *
 * Handles:
 * - Strings: return as-is (already CSS values, includes variables)
 * - Type objects: use appropriate converter (FontFamily, FontWeight, etc.)
 * - CSS Variables: use CssVariable.toCSSValue
 */
function partialToCSSValue(
  value: unknown,
  converter?: typeof FontFamily | typeof FontWeight,
): string | null {
  if (value == null) {
    return null;
  }

  // Already a CSS string - preserve as-is (includes CSS variables like "var(--color)")
  if (typeof value === 'string') {
    return value;
  }

  // Not a string - must be a type object
  if (typeof value === 'object' && value !== null) {
    // CSS Variable object - use CssVariable.toCSSValue (same as parser.ts pattern)
    if ('type' in value && value.type === 'variable') {
      try {
        return CssVariable.toCSSValue(
          value as Parameters<typeof CssVariable.toCSSValue>[0],
        );
      } catch {
        return String(value);
      }
    }

    // Font type object - use provided converter (same as parser.ts pattern)
    if (converter) {
      try {
        return converter.toCSSValue(value as any) ?? null;
      } catch {
        return String(value);
      }
    }
  }

  // Fallback: convert to string
  return String(value);
}

/**
 * Check if partials object has any properties set.
 * Both undefined and empty object are treated as not existed.
 */
function hasPartials(partials?: TextControlPartials): boolean {
  if (!partials) {
    return false;
  }
  // Check if at least one font-related partial property exists
  return (
    partials.fontFamily != null ||
    partials.fontSize != null ||
    partials.fontWeight != null ||
    partials.fontStyle != null
  );
}

/**
 * Event mapper: TextControlGroup onChange → CSS property changes
 *
 * New contract:
 * 1. Partials can be undefined or empty object (both treated as not existed)
 * 2. Color is NOT a partial - it's a standalone value (like font)
 * 3. Handle partials if not empty, else handle standardValue based on variableType
 * 4. namespaceTracker routing works as before
 *
 * Handles:
 * - Partials-based mapping (font decomposition: fontFamily, fontSize, fontWeight, fontStyle)
 * - Standalone values (color, font, and other properties via standardValue)
 * - Namespace routing (cssProperties vs cssCustomProperties via namespaceTracker)
 */
export const mapTextEvent = (
  _definitionType: DefinitionType,
  variableType: VariableType,
  standardValue: string,
  namespaceTracker: NamespaceTracker,
  partials?: TextControlPartials,
): {
  cssProperties?: Record<string, string>;
  cssCustomProperties?: Record<string, string>;
} => {
  // Check if we have partials with actual values
  const usePartials = hasPartials(partials);

  if (usePartials) {
    // Handle font partials (decomposed font properties)
    const stringValues: Record<string, string> = {};

    // Convert each partial using appropriate converter
    if (partials!.fontFamily != null) {
      const cssValue = partialToCSSValue(partials!.fontFamily, FontFamily);
      if (cssValue) {
        stringValues.fontFamily = cssValue;
      }
    }
    if (partials!.fontSize != null) {
      const cssValue = partialToCSSValue(partials!.fontSize);
      if (cssValue) {
        stringValues.fontSize = cssValue;
      }
    }
    if (partials!.fontWeight != null) {
      const cssValue = partialToCSSValue(partials!.fontWeight, FontWeight);
      if (cssValue) {
        stringValues.fontWeight = cssValue;
      }
    }
    if (partials!.fontStyle != null) {
      const cssValue = partialToCSSValue(partials!.fontStyle);
      if (cssValue) {
        stringValues.fontStyle = cssValue;
      }
    }

    // Route each property to correct namespace based on namespaceTracker
    const cssProperties: Record<string, string> = {};
    const cssCustomProperties: Record<string, string> = {};

    Object.entries(stringValues).forEach(([property, value]) => {
      const originalKey = namespaceTracker[property];
      if (originalKey) {
        cssCustomProperties[originalKey] = value;
      } else {
        cssProperties[property] = value;
      }
    });

    return {
      ...(Object.keys(cssProperties).length > 0 && { cssProperties }),
      ...(Object.keys(cssCustomProperties).length > 0 && {
        cssCustomProperties,
      }),
    };
  }

  // Handle standalone values (color, font, and other properties)
  if (variableType.category !== 'cssPropertyType') {
    console.error('Unsupported variable type:', variableType);
    return {};
  }

  const propertyKey = variableType.cssPropertyType as string;

  // Route to correct namespace based on namespaceTracker
  const originalKey = namespaceTracker[propertyKey];
  if (originalKey) {
    return {
      cssCustomProperties: { [originalKey]: standardValue },
    };
  }

  return {
    cssProperties: { [propertyKey]: standardValue },
  };
};
